import { useQuery } from '@tanstack/react-query';
import { QueryResult } from 'components/query-result/query-result';
import {
  ListSubscriptionItem,
  ListSubscriptionsResponse,
} from 'generated/api-client';
import { listSubscriptionsOptions } from 'generated/api-client/@tanstack/react-query.gen';
import React, { FunctionComponent } from 'react';
import { Link } from 'react-router-dom';
import { Message, Table } from 'semantic-ui-react';
import {
  formatAmount,
  formatCalendarDate,
  formatCalendarMonth,
  formatDate,
  ON_DEMAND_QUERY_OPTIONS,
} from 'utils';

// « 105,00 € pendant 3 mois, puis 55,00 € pendant 3 mois »
const formatDiscounts = ({ discounts }: ListSubscriptionItem): string =>
  discounts
    .map(
      (discount) =>
        `${formatAmount(discount.amount)} pendant ${discount.durationInMonths} mois`,
    )
    .join(', puis ');

const formatSetupFee = ({ setupFee }: ListSubscriptionItem): string => {
  if (!setupFee) return 'Aucun';

  const amount = formatAmount(setupFee.amount);
  return setupFee.durationInMonths > 1
    ? `${amount} sur ${setupFee.durationInMonths} mois`
    : amount;
};

type Props = {
  companyId: number;
};

const SubscriptionsList: FunctionComponent<{
  data: ListSubscriptionsResponse;
}> = ({ data: { subscriptions, unreadableSubscriptions } }) => {
  // The ones to correct first
  const unreadable = unreadableSubscriptions.length > 0 && (
    // Visible: a form hides its warnings otherwise
    <Message warning visible>
      <Message.Header>Abonnements illisibles, à corriger</Message.Header>
      <Message.List>
        {unreadableSubscriptions.map((subscription) => (
          <Message.Item key={subscription.viewId}>
            <Link to={`/pros/update/${subscription.viewId}`}>
              {subscription.viewId}
            </Link>{' '}
            : {subscription.error}
          </Message.Item>
        ))}
      </Message.List>
    </Message>
  );

  if (subscriptions.length === 0) {
    return (
      <>
        {unreadable}
        <p>{"Il n'y a pas d'abonnement"}</p>
      </>
    );
  }

  return (
    <>
      {unreadable}
      <Table basic="very" compact selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Fiche</Table.HeaderCell>
            <Table.HeaderCell>Montant HT</Table.HeaderCell>
            <Table.HeaderCell>Remises</Table.HeaderCell>
            <Table.HeaderCell>Droit d&apos;entrée HT</Table.HeaderCell>
            <Table.HeaderCell>Date de début</Table.HeaderCell>
            <Table.HeaderCell>Prochaine facture</Table.HeaderCell>
            <Table.HeaderCell>Créé le</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {subscriptions.map((subscription) => (
            <Table.Row key={subscription.viewId}>
              <Table.Cell>
                <Link to={`/pros/update/${subscription.viewId}`}>
                  {subscription.viewId}
                </Link>{' '}
                · {subscription.zoneName} · {subscription.tradeNames}
              </Table.Cell>
              <Table.Cell>{formatAmount(subscription.baseAmount)}</Table.Cell>
              <Table.Cell>
                {formatDiscounts(subscription) || 'Aucune'}
              </Table.Cell>
              <Table.Cell>{formatSetupFee(subscription)}</Table.Cell>
              <Table.Cell>
                {formatCalendarDate(subscription.startDate)}
              </Table.Cell>
              <Table.Cell>
                {formatCalendarMonth(subscription.nextInvoiceMonth)}
              </Table.Cell>
              <Table.Cell>{formatDate(subscription.createdAt)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </>
  );
};

export const SubscriptionsTable: FunctionComponent<Props> = ({ companyId }) => {
  const query = useQuery(
    Object.assign(
      listSubscriptionsOptions({ query: { companyId } }),
      ON_DEMAND_QUERY_OPTIONS,
    ),
  );

  return (
    <QueryResult query={query} label="abonnements">
      {(data): JSX.Element => <SubscriptionsList data={data} />}
    </QueryResult>
  );
};
